import { useState, useRef, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface Prediction {
  place_id: string;
  description: string;
}

interface AddressDetails {
  address: string;
  postalCode: string;
  city: string;
  countryCode: string;
}

interface UseAddressAutocompleteProps {
  onSelect: (details: AddressDetails) => void;
}

export function useAddressAutocomplete({ onSelect }: UseAddressAutocompleteProps) {
  const [query, setQuery] = useState('');
  const [predictions, setPredictions] = useState<Prediction[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const sessionTokenRef = useRef(crypto.randomUUID());
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  const search = useCallback(async (input: string) => {
    if (input.length < 3) {
      setPredictions([]);
      setIsOpen(false);
      return;
    }
    setIsLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('google-places', {
        body: {
          action: 'autocomplete',
          input,
          sessionToken: sessionTokenRef.current,
        },
      });
      if (!error && data?.predictions) {
        setPredictions(data.predictions);
        setIsOpen(data.predictions.length > 0);
      }
    } catch {
      // silent
    } finally {
      setIsLoading(false);
    }
  }, []);

  const handleInputChange = useCallback((value: string) => {
    setQuery(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => search(value), 300);
  }, [search]);

  const handleSelect = useCallback(async (prediction: Prediction) => {
    setQuery(prediction.description);
    setIsOpen(false);
    setPredictions([]);

    try {
      const { data, error } = await supabase.functions.invoke('google-places', {
        body: {
          action: 'details',
          placeId: prediction.place_id,
          sessionToken: sessionTokenRef.current,
        },
      });

      // Reset session token after place details request
      sessionTokenRef.current = crypto.randomUUID();

      if (!error && data?.result?.address_components) {
        const components = data.result.address_components as Array<{
          long_name: string;
          short_name: string;
          types: string[];
        }>;

        const get = (type: string) =>
          components.find((c) => c.types.includes(type));

        const streetNumber = get('street_number')?.long_name || '';
        const route = get('route')?.long_name || '';
        const postalCode = get('postal_code')?.long_name || '';
        const city =
          get('locality')?.long_name ||
          get('postal_town')?.long_name ||
          get('administrative_area_level_1')?.long_name ||
          '';
        const countryCode = get('country')?.short_name || 'CH';

        const address = streetNumber ? `${route} ${streetNumber}` : route;

        onSelect({ address, postalCode, city, countryCode });
        setQuery(address);
      }
    } catch {
      // silent
    }
  }, [onSelect]);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  return {
    query,
    setQuery,
    predictions,
    isOpen,
    setIsOpen,
    isLoading,
    handleInputChange,
    handleSelect,
  };
}
